import React from 'react';
import { Link } from 'react-router-dom';
import ArticlePage from './ArticlePage';

function MCPAgentsPage() {
  const articleContent = (
    <>
      <h2>The Next Frontier: AI Agents That Reason with Models</h2>
      <p>
        Large language models excel at understanding context and generating natural language responses.
        Simulation models excel at projecting complex system behavior over time. Until now, these
        capabilities have operated in isolation. <a href="https://modelcontextprotocol.io/docs/getting-started/intro" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb' }}>Model Context Protocol (MCP)</a> changes that-enabling
        AI agents to directly query, manipulate, and reason with simulation models.
      </p>
      <p>
        This integration transforms AI-powered decision support from simple conversational assistance to
        genuine strategic reasoning. By connecting the "Pattern Matching" of an LLM with the "Causal Math"
        of a simulation, organizations can move from asking "What happened?" to "What should we do next?"
      </p>

      <hr style={{ margin: '2rem 0', border: 0, borderTop: '1px solid #e5e7eb' }} />

      <h2>The Veydra Technical Stack: The Simulation Core</h2>
      <p>
        To understand how an agent "reasons," we must look at the specialized components Veydra provides
        to the AI ecosystem.
      </p>
      <ul>
        <li>
          <strong>The Veydra Engine (The SaaS Source of Truth):</strong> Located at app.veydra.io, this is
          the heavy-lift environment where your system dynamics and causal models live. It manages versioned
          system models and defines the "knobs" (parameters) an AI is allowed to turn. For enterprise
          deployments, the same engine can also run in a preferred cloud environment or on-premises.
        </li>
        <li>
          <strong>The Veydra MCP Server (The Gateway):</strong> This is the technical "translator" that
          makes the Engine's math legible to an AI. It exposes tools like <code>run_simulation</code> and
          <code>get_parameters</code> through a standardized JSON-RPC interface, eliminating the need for
          custom glue code.
        </li>
        <li>
          <strong>The Causal Memory Layer (The System Map):</strong> Veydra provides agents with more than
          just raw numbers; it provides <strong>Causality</strong>. It maps feedback loops, allowing the
          agent to see how a change in one variable ripples through the entire system over time.
        </li>
      </ul>

      <hr style={{ margin: '2rem 0', border: 0, borderTop: '1px solid #e5e7eb' }} />

      <h2>Implementation: The Consultant Pattern in Action</h2>
      <p>
        By integrating Veydra into an enterprise environment like Microsoft Azure, the AI shifts from a
        basic assistant to a high-level consultant.
      </p>

      <h3>1. The Everyday User (Inside Microsoft Teams)</h3>
      <p>
        <strong>Entry Point:</strong> An employee asks a question to the company&apos;s internal Copilot via
        Copilot Studio.
      </p>
      <ul>
        <li>
          <strong>Standard AI:</strong> "Based on SharePoint documents, our standard lead time for parts is
          14 days. You should order now for the April deadline."
        </li>
        <li>
          <strong>Veydra-Integrated AI:</strong> "I&apos;ve simulated the April deadline. While the nominal
          lead time is 14 days, Veydra shows a regional freight bottleneck likely to cause a 10-day delay.
          <strong> Recommendation:</strong> Order 24 days in advance and increase safety stock by 5% to avoid
          a stockout."
        </li>
      </ul>

      <h3>2. The Agentic Orchestrator (Inside Azure AI Foundry)</h3>
      <p>
        <strong>Entry Point:</strong> A multi-agent workflow where a Purchasing Agent and a Finance Agent
        collaborate using <strong>Agent-to-Agent (A2A)</strong> protocols.
      </p>
      <ul>
        <li>
          <strong>Standard AI:</strong> The Purchasing Agent sees low stock and automatically generates a
          purchase order (PO).
        </li>
        <li>
          <strong>Veydra-Integrated AI:</strong> The Purchasing Agent calls the Veydra MCP Server for a
          pre-action check. Veydra simulates the cash flow impact, triggering the Finance Agent to intervene:
          "Wait. Veydra shows this PO will drop our cash-on-hand below the required reserve during tax week.
          Let&apos;s split this into two smaller shipments."
        </li>
      </ul>

      <hr style={{ margin: '2rem 0', border: 0, borderTop: '1px solid #e5e7eb' }} />

      <h2>The Unified Veydra and Enterprise Agent Architecture</h2>

      <h3>I. Experience and Interaction Layer (The User Interface)</h3>
      <ul>
        <li>
          <strong>The Logic:</strong> Transforming natural language intent into structured system queries.
        </li>
        <li>
          <strong>The Tech:</strong> <strong>M365 Copilot</strong> or <strong>Gemini for Workspace</strong>
          connects to the Veydra Cloud Gateway.
        </li>
        <li>
          <strong>The Capability:</strong> <strong>Instant What-If Dashboards.</strong> Users get interactive
          projections in chat. They can adjust sliders (parameters) to see real-time system sensitivity
          directly within their communication tools.
        </li>
      </ul>

      <h3>II. Orchestration and Tooling Layer (The Logic and Hands)</h3>
      <ul>
        <li>
          <strong>The Logic:</strong> Moving from text completion to validated planning.
        </li>
        <li>
          <strong>The Tech:</strong> <strong>Azure AI Foundry</strong> or <strong>Vertex AI Agent Builder</strong>
          serves as the host. The <strong>Veydra MCP Server</strong> provides tools, while
          <strong> A2A</strong> protocols allow specialized bots to debate results.
        </li>
        <li>
          <strong>The Capability:</strong> <strong>Validated Autonomous Actions.</strong> Before an agent
          triggers an ERP order, it runs a Veydra simulation. If the simulation predicts a system break, the
          agent pauses for human approval.
        </li>
      </ul>

      <h3>III. Reasoning and Simulation Layer (The Causal Brain)</h3>
      <ul>
        <li>
          <strong>The Logic:</strong> Grounding the LLM&apos;s creativity in the reality of
          <strong> System Dynamics</strong>.
        </li>
        <li>
          <strong>The Tech:</strong> <strong>Frontier reasoning models</strong> provide reasoning, while the
          <strong> Veydra Engine</strong> provides causal maps.
        </li>
        <li>
          <strong>The Capability:</strong> <strong>Causal Fact-Checking.</strong> This layer prevents
          hallucinations. It ensures AI logic follows the actual math of your business system-such as lead
          times and resource constraints-rather than likely-sounding text.
        </li>
      </ul>

      <h3>IV. Governance and Security Layer (The Guardrails)</h3>
      <ul>
        <li>
          <strong>The Logic:</strong> Managing non-human identities and auditing the logic behind every
          decision.
        </li>
        <li>
          <strong>The Tech:</strong> <strong>Microsoft Entra Agent ID</strong> and <strong>Purview </strong>
          manage permissions and data protection.
        </li>
        <li>
          <strong>The Capability:</strong> <strong>Risk-Adjusted Autonomy.</strong> Security officers can set
          policies where an agent&apos;s authority is granted based on simulation confidence score, creating an
          audit trail of why specific actions were taken.
        </li>
      </ul>

      <h3>V. CLI and Developer Layer (The Forge)</h3>
      <ul>
        <li>
          <strong>The Logic:</strong> Simulation-Driven Development-treating the system model as a unit test
          for AI.
        </li>
        <li>
          <strong>The Tech:</strong> <strong>Azure AI CLI (<code>az ai agent</code>)</strong> and
          <strong> Veydra MCP CLI (<code>npx @veydra-io/mcp-server</code>)</strong>.
        </li>
        <li>
          <strong>The Capability:</strong> <strong>Simulation-Based Unit Testing.</strong> Developers run
          batch simulations to test how an agent handles 1,000 different crisis scenarios before pushing to
          production.
        </li>
      </ul>

      <hr style={{ margin: '2rem 0', border: 0, borderTop: '1px solid #e5e7eb' }} />

      <h2>Implications for Decision Support</h2>
      <p>
        MCP integration fundamentally changes the ROI of AI:
      </p>
      <ol>
        <li><strong>Reliability:</strong> Decisions are grounded in system dynamics math, not just word probabilities.</li>
        <li><strong>Safer Autonomy:</strong> Agents can look before they leap by simulating outcomes in a digital sandbox.</li>
        <li><strong>Faster Collaboration:</strong> Business users, operators, and developers work from the same model-backed context.</li>
      </ol>
      <p style={{ marginTop: '1.5rem' }}>
        Learn how to connect Veydra&apos;s MCP to your AI environment on our <Link to="/mcp" style={{ color: '#2563eb' }}>MCP Integration Page</Link>.
      </p>

    </>
  );

  const relatedArticles = [
    { category: 'Article', title: 'AI-Enhanced Simulation Modeling', href: '/innovation/ai-simulation' },
    { category: 'Article', title: 'Why AI Needs Simulation (and Vice Versa)', href: '/innovation/ai-meets-simulation' },
  ];

  return (
    <ArticlePage
      category="Article"
      title="AI Agents and MCP: The Next Frontier in Simulation"
      subtitle="How Model Context Protocol enables AI agents to reason with simulation models-a technical deep-dive into intelligent decision support."
      articleContent={articleContent}
      relatedArticles={relatedArticles}
    />
  );
}

export default MCPAgentsPage;
